'use client';

import * as React from 'react';
import { Card, cn } from '@strivelabs/ui-core';
import { Button } from '@strivelabs/ui-core/v2';
import { ChevronDown, ListTodo, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';
import type { HTMLMotionProps } from 'motion/react';
import type {
  AgentKeyFinding,
  AgentKeyFindingActionItem,
  AgentKeyFindingsLayoutVariant,
} from '../../types';
import { RichTextRenderer } from '../rich-text';

const SECTION_SHELL = 'mx-auto w-full max-w-[600px] min-w-0 sm:min-w-[600px] sm:w-[600px]';

const markdownP =
  '[&_p]:m-0 [&_p]:text-sm [&_p]:leading-relaxed [&_p]:text-custom-text-primary';
const markdownTitle =
  '[&_p]:m-0 [&_p]:text-sm [&_p]:font-semibold [&_p]:leading-snug [&_p]:text-custom-text-primary';

const listContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.04 },
  },
};

const listItem = {
  hidden: { opacity: 0, x: -6 },
  show: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.3, ease: [0.25, 0.1, 0.25, 1] as const },
  },
};

export interface AgentKeyFindingsCardProps {
  /** Section title above the cards. Defaults to “Action items” for `actionChecklist` / `agentActionCards`. */
  title?: string;
  findings: AgentKeyFinding[];
  /** Presentation style; defaults to `stacked`. */
  layout?: AgentKeyFindingsLayoutVariant;
  className?: string;
  /** Fired when the primary CTA is pressed (`agentActionCards` layout). */
  onPrimaryCta?: (findingId: string) => void;
  /** Fired when the secondary CTA is pressed (`agentActionCards` layout). */
  onSecondaryCta?: (findingId: string) => void;
  /**
   * When set, each action with a `sourceStepId` (or `actionSourceStepIds` / finding `sourceStepId`) shows a Deep dive pill that opens the agent run UI at that step.
   */
  onActionSourceClick?: (sourceStepId: string) => void;
  /** Label for the deep-dive pill (default `Deep dive`). */
  actionSourceLabel?: string;
}

const DEFAULT_TITLE = 'Key Findings & Recommendations';
const ACTION_ITEMS_TITLE = 'Action items';
const DEFAULT_ACTION_SOURCE_LABEL = 'Deep dive';

/** Small gray pill to open the agent run dialog at a step (`AgentStep.id`). */
function DeepDivePill({
  stepId,
  onActionSourceClick,
  label,
  className,
}: {
  stepId?: string | null;
  onActionSourceClick?: (sourceStepId: string) => void;
  label: string;
  className?: string;
}) {
  if (!stepId?.trim() || !onActionSourceClick) return null;
  const id = stepId.trim();
  return (
    <Button
      variant="secondary"
      size="xs"
      className={cn('shrink-0', className)}
      aria-label={`${label}: open agent run at this step`}
      onClick={(e) => {
        e.stopPropagation();
        onActionSourceClick(id);
      }}
    >
      {label}
    </Button>
  );
}

const cardInteractive =
  'transition-all duration-300 ease-out hover:shadow-md hover:border-rose-200/40 dark:hover:border-rose-900/50 focus-within:ring-2 focus-within:ring-rose-200/60 focus-within:ring-offset-2 focus-within:ring-offset-background dark:focus-within:ring-rose-900/40';

/** Decorative square checkbox (visual only). */
function DecorativeCheckbox({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'mt-0.5 inline-block h-4 w-4 shrink-0 rounded border border-zinc-300 bg-white dark:border-zinc-600 dark:bg-zinc-900',
        className,
      )}
      aria-hidden
    />
  );
}

function flattenChecklistRows(
  findings: AgentKeyFinding[],
): { id: string; text: string; sourceStepId?: string }[] {
  const rows: { id: string; text: string; sourceStepId?: string }[] = [];
  for (const f of findings) {
    if (f.actionItems?.length) {
      f.actionItems.forEach((item, i) => {
        if (item.title.trim()) {
          rows.push({
            id: `${f.id}-chk-${i}`,
            text: item.title,
            sourceStepId: item.sourceStepId,
          });
        }
      });
    } else if (f.actions.length > 0) {
      f.actions.forEach((text, i) => {
        if (text.trim()) {
          rows.push({
            id: `${f.id}-chk-${i}`,
            text,
            sourceStepId: f.actionSourceStepIds?.[i],
          });
        }
      });
    } else if (f.title.trim()) {
      rows.push({ id: f.id, text: f.title, sourceStepId: f.sourceStepId });
    }
  }
  return rows;
}

function actionRowsForFinding(f: AgentKeyFinding): AgentKeyFindingActionItem[] {
  if (f.actionItems?.length) {
    return f.actionItems.filter((a) => a.title.trim());
  }
  return f.actions
    .filter((t) => t.trim())
    .map((title, i) => ({
      title,
      sourceStepId: f.actionSourceStepIds?.[i],
    }));
}

/** Flat rows for task-style layouts (checkbox + title + optional summary). */
function flattenTaskRows(
  findings: AgentKeyFinding[],
): (AgentKeyFindingActionItem & { id: string })[] {
  const rows: (AgentKeyFindingActionItem & { id: string })[] = [];
  for (const f of findings) {
    actionRowsForFinding(f).forEach((row, i) => {
      rows.push({ id: `${f.id}-task-${i}`, ...row });
    });
  }
  return rows;
}

const taskTitleMd =
  '[&_p]:m-0 [&_p]:text-sm [&_p]:font-semibold [&_p]:leading-snug [&_p]:text-custom-text-primary';
const taskSummaryMd =
  '[&_p]:m-0 [&_p]:text-xs [&_p]:leading-relaxed [&_p]:text-muted-foreground';

/** Single gradient card: checkbox + title + optional summary per row (reference: todo / briefing list). */
function TaskRowsLayout({
  title,
  rows,
  motionProps,
  reduceMotion,
  onActionSourceClick,
  actionSourceLabel,
}: {
  title: string;
  rows: (AgentKeyFindingActionItem & { id: string })[];
  motionProps: Pick<HTMLMotionProps<'div'>, 'initial' | 'animate' | 'transition'>;
  reduceMotion: boolean;
  onActionSourceClick?: (sourceStepId: string) => void;
  actionSourceLabel: string;
}) {
  if (rows.length === 0) return null;

  return (
    <motion.div {...motionProps}>
      <Card
        className={cn(
          'rounded-2xl border border-custom-border bg-gradient-to-b from-sky-50/90 via-background to-indigo-50/45 p-5 shadow-sm dark:from-sky-950/35 dark:via-background dark:to-indigo-950/30',
          cardInteractive,
        )}
      >
        <h2 className="text-lg font-bold text-custom-text-primary">{title}</h2>
        <ul className="mt-5 space-y-5" role="list">
          {rows.map((row, index) => (
            <motion.li
              key={row.id}
              className="flex gap-3"
              initial={reduceMotion ? false : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: reduceMotion ? 0 : 0.04 * index, duration: 0.25 }}
            >
              <DecorativeCheckbox className="mt-1 border-sky-400/45 dark:border-sky-600/45" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <RichTextRenderer content={row.title} className={taskTitleMd} />
                  </div>
                  {!row.summary?.trim() ? (
                    <DeepDivePill
                      stepId={row.sourceStepId}
                      onActionSourceClick={onActionSourceClick}
                      label={actionSourceLabel}
                    />
                  ) : null}
                </div>
                {row.summary?.trim() ? (
                  <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-1.5">
                    <div className="min-w-0 flex-1">
                      <RichTextRenderer content={row.summary} className={taskSummaryMd} />
                    </div>
                    <DeepDivePill
                      stepId={row.sourceStepId}
                      onActionSourceClick={onActionSourceClick}
                      label={actionSourceLabel}
                      className="shrink-0"
                    />
                  </div>
                ) : null}
              </div>
            </motion.li>
          ))}
        </ul>
      </Card>
    </motion.div>
  );
}

/** Each action as its own card: checkbox + title + optional summary. */
function TaskCardsLayout({
  rows,
  itemMotion,
  onActionSourceClick,
  actionSourceLabel,
}: {
  rows: (AgentKeyFindingActionItem & { id: string })[];
  itemMotion: (i: number) => Pick<HTMLMotionProps<'div'>, 'initial' | 'animate' | 'transition'>;
  onActionSourceClick?: (sourceStepId: string) => void;
  actionSourceLabel: string;
}) {
  return (
    <>
      {rows.map((row, i) => (
        <motion.div key={row.id} {...itemMotion(i)}>
          <Card
            className={cn(
              'overflow-hidden rounded-2xl border border-custom-border bg-gradient-to-b from-sky-50/85 to-background/98 p-4 shadow-sm dark:from-sky-950/30 dark:to-background',
              cardInteractive,
            )}
          >
            <div className="flex gap-3">
              <DecorativeCheckbox className="mt-0.5 border-sky-400/45 dark:border-sky-600/45" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <RichTextRenderer content={row.title} className={taskTitleMd} />
                  </div>
                  {!row.summary?.trim() ? (
                    <DeepDivePill
                      stepId={row.sourceStepId}
                      onActionSourceClick={onActionSourceClick}
                      label={actionSourceLabel}
                    />
                  ) : null}
                </div>
                {row.summary?.trim() ? (
                  <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-1.5">
                    <div className="min-w-0 flex-1">
                      <RichTextRenderer content={row.summary} className={taskSummaryMd} />
                    </div>
                    <DeepDivePill
                      stepId={row.sourceStepId}
                      onActionSourceClick={onActionSourceClick}
                      label={actionSourceLabel}
                      className="shrink-0"
                    />
                  </div>
                ) : null}
              </div>
            </div>
          </Card>
        </motion.div>
      ))}
    </>
  );
}

/** Single white card: title + flat checklist (reference: minimalist action list). */
function ActionChecklistLayout({
  title,
  findings,
  motionProps,
  reduceMotion,
  onActionSourceClick,
  actionSourceLabel,
}: {
  title: string;
  findings: AgentKeyFinding[];
  motionProps: Pick<HTMLMotionProps<'div'>, 'initial' | 'animate' | 'transition'>;
  reduceMotion: boolean;
  onActionSourceClick?: (sourceStepId: string) => void;
  actionSourceLabel: string;
}) {
  const rows = flattenChecklistRows(findings);
  if (rows.length === 0) return null;

  return (
    <motion.div {...motionProps}>
      <Card
        className={cn(
          'rounded-2xl border border-custom-border bg-background p-5 shadow-sm',
          cardInteractive,
        )}
      >
        <h2 className="text-lg font-bold text-custom-text-primary">{title}</h2>
        <ul className="mt-5 space-y-4" role="list">
          {rows.map((row, index) => (
            <motion.li
              key={row.id}
              className="flex gap-3 text-sm leading-relaxed text-custom-text-primary"
              initial={reduceMotion ? false : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: reduceMotion ? 0 : 0.04 * index, duration: 0.25 }}
            >
              <DecorativeCheckbox />
              <div className="flex min-w-0 flex-1 items-start justify-between gap-2">
                <span className="min-w-0 flex-1">{row.text}</span>
                <DeepDivePill
                  stepId={row.sourceStepId}
                  onActionSourceClick={onActionSourceClick}
                  label={actionSourceLabel}
                />
              </div>
            </motion.li>
          ))}
        </ul>
      </Card>
    </motion.div>
  );
}

/** Peach header + AI body + dual CTAs per finding (reference: agent action cards). */
function AgentActionCardsLayout({
  findings,
  itemMotion,
  onPrimaryCta,
  onSecondaryCta,
  onActionSourceClick,
  actionSourceLabel,
}: {
  findings: AgentKeyFinding[];
  itemMotion: (i: number) => Pick<HTMLMotionProps<'div'>, 'initial' | 'animate' | 'transition'>;
  onPrimaryCta?: (findingId: string) => void;
  onSecondaryCta?: (findingId: string) => void;
  onActionSourceClick?: (sourceStepId: string) => void;
  actionSourceLabel: string;
}) {
  return (
    <>
      {findings.map((finding, i) => {
        const body =
          finding.recommendation?.trim() || finding.description?.trim() || '';
        const showFooter =
          Boolean(finding.primaryCtaLabel?.trim()) ||
          Boolean(finding.secondaryCtaLabel?.trim());

        return (
          <motion.div key={finding.id} {...itemMotion(i)}>
            <Card className="overflow-hidden rounded-2xl border border-custom-border bg-background shadow-sm transition-shadow duration-300 hover:shadow-md">
              <div className="flex gap-3 border-b border-orange-100/90 bg-[#FFF7F2] px-4 py-3.5 dark:border-orange-950/30 dark:bg-orange-950/20">
                <DecorativeCheckbox className="mt-1 border-orange-200/80" />
                <div className="flex min-w-0 flex-1 items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <RichTextRenderer
                      content={finding.title}
                      className="[&_p]:m-0 [&_p]:text-sm [&_p]:font-semibold [&_p]:leading-snug [&_p]:text-custom-text-primary"
                    />
                  </div>
                  <DeepDivePill
                    stepId={finding.sourceStepId}
                    onActionSourceClick={onActionSourceClick}
                    label={actionSourceLabel}
                  />
                </div>
              </div>

              {body ? (
                <div className="flex gap-2.5 px-4 py-4">
                  <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-pink-500" aria-hidden />
                  <RichTextRenderer
                    content={body}
                    className="[&_p]:m-0 [&_p]:text-sm [&_p]:leading-relaxed [&_p]:text-custom-text-primary"
                  />
                </div>
              ) : null}

              {showFooter ? (
                <div className="flex flex-wrap justify-end gap-2 border-t border-custom-border/60 px-4 py-3">
                  {finding.secondaryCtaLabel?.trim() ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onSecondaryCta?.(finding.id)}
                    >
                      {finding.secondaryCtaLabel}
                    </Button>
                  ) : null}
                  {finding.primaryCtaLabel?.trim() ? (
                    <Button
                      size="sm"
                      onClick={() => onPrimaryCta?.(finding.id)}
                    >
                      {finding.primaryCtaLabel}
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </Card>
          </motion.div>
        );
      })}
    </>
  );
}

function StackedFinding({
  finding,
  motionProps,
  reduceMotion,
  onActionSourceClick,
  actionSourceLabel,
}: {
  finding: AgentKeyFinding;
  motionProps: Pick<HTMLMotionProps<'div'>, 'initial' | 'animate' | 'transition'>;
  reduceMotion: boolean;
  onActionSourceClick?: (sourceStepId: string) => void;
  actionSourceLabel: string;
}) {
  const actionRows = actionRowsForFinding(finding);

  return (
    <motion.div {...motionProps}>
      <Card
        className={cn(
          'group/card overflow-hidden rounded-xl border-custom-border bg-gradient-to-b from-custom-bg-subtle/90 to-background/80 shadow-sm backdrop-blur-[1px] dark:from-zinc-900/50 dark:to-zinc-950/40',
          cardInteractive,
        )}
      >
        <div className="border-b border-orange-100/90 bg-gradient-to-r from-orange-50 via-amber-50/80 to-orange-50/40 px-4 py-3.5 dark:border-orange-950/40 dark:from-orange-950/40 dark:via-amber-950/25 dark:to-orange-950/20">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <RichTextRenderer content={finding.title} className={markdownTitle} />
            </div>
            <DeepDivePill
              stepId={finding.sourceStepId}
              onActionSourceClick={onActionSourceClick}
              label={actionSourceLabel}
            />
          </div>
        </div>

        <div className="space-y-4 px-4 py-4">
          <RichTextRenderer content={finding.description} className={markdownP} />

          {actionRows.length > 0 ? (
            <motion.ul
              className="space-y-1"
              variants={listContainer}
              initial={reduceMotion ? false : 'hidden'}
              animate="show"
            >
              {actionRows.map((action, index) => (
                <motion.li
                  key={`${finding.id}-action-${index}`}
                  variants={listItem}
                  className="group/action flex gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-orange-50/60 dark:hover:bg-orange-950/25"
                >
                  <Sparkles
                    className="mt-0.5 h-4 w-4 shrink-0 text-amber-500 transition-transform duration-200 group-hover/action:scale-110 group-hover/action:text-amber-600 dark:text-amber-400"
                    aria-hidden
                  />
                  <div className="flex min-w-0 flex-1 items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <RichTextRenderer content={action.title} className={markdownP} />
                    </div>
                    <DeepDivePill
                      stepId={action.sourceStepId}
                      onActionSourceClick={onActionSourceClick}
                      label={actionSourceLabel}
                    />
                  </div>
                </motion.li>
              ))}
            </motion.ul>
          ) : null}
        </div>
      </Card>
    </motion.div>
  );
}

/** Title + narrative; collapsible todo-style actions. */
function TodoListFinding({
  finding,
  motionProps,
  onActionSourceClick,
  actionSourceLabel,
}: {
  finding: AgentKeyFinding;
  motionProps: Pick<HTMLMotionProps<'div'>, 'initial' | 'animate' | 'transition'>;
  onActionSourceClick?: (sourceStepId: string) => void;
  actionSourceLabel: string;
}) {
  const actionRows = actionRowsForFinding(finding);

  return (
    <motion.div {...motionProps}>
      <Card
        className={cn(
          'rounded-xl border-custom-border bg-background/95 p-4 shadow-sm backdrop-blur-sm',
          cardInteractive,
        )}
      >
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <RichTextRenderer
              content={finding.title}
              className="[&_p]:m-0 [&_p]:text-base [&_p]:font-semibold [&_p]:leading-snug [&_p]:text-custom-text-primary"
            />
          </div>
          <DeepDivePill
            stepId={finding.sourceStepId}
            onActionSourceClick={onActionSourceClick}
            label={actionSourceLabel}
          />
        </div>
        <div className="mt-3">
          <RichTextRenderer content={finding.description} className={markdownP} />
        </div>

        {actionRows.length > 0 ? (
          <details className="group/details mt-4 overflow-hidden rounded-xl border border-dashed border-custom-border/80 bg-muted/25 open:bg-muted/40 open:shadow-inner dark:bg-zinc-900/40">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground transition-colors hover:bg-accent/50 hover:text-custom-text-primary [&::-webkit-details-marker]:hidden">
              <span>Recommended actions</span>
              <span className="flex items-center gap-1 text-[10px] font-normal normal-case text-zinc-500">
                {actionRows.length} items
                <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 group-open/details:rotate-180" />
              </span>
            </summary>
            <div className="border-t border-custom-border/60 px-3 pb-3 pt-1">
              <ul className="space-y-1">
                {actionRows.map((action, index) => (
                  <li
                    key={`${finding.id}-todo-${index}`}
                    className="group/todo flex gap-2.5 rounded-md px-2 py-2 transition-colors hover:bg-background/80"
                  >
                    <ListTodo
                      className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 transition-transform duration-200 group-hover/todo:scale-110 dark:text-amber-500"
                      aria-hidden
                    />
                    <div className="flex min-w-0 flex-1 items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <RichTextRenderer content={action.title} className={markdownP} />
                      </div>
                      <DeepDivePill
                        stepId={action.sourceStepId}
                        onActionSourceClick={onActionSourceClick}
                        label={actionSourceLabel}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </details>
        ) : null}
      </Card>
    </motion.div>
  );
}

/** Narrative on top; each action as an interactive small card. */
function ActionCardsFindingWithMotion({
  finding,
  reduceMotion,
  onActionSourceClick,
  actionSourceLabel,
}: {
  finding: AgentKeyFinding;
  reduceMotion: boolean;
  onActionSourceClick?: (sourceStepId: string) => void;
  actionSourceLabel: string;
}) {
  const actionRows = actionRowsForFinding(finding);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <RichTextRenderer
            content={finding.title}
            className="[&_p]:m-0 [&_p]:text-base [&_p]:font-semibold [&_p]:text-custom-text-primary"
          />
        </div>
        <DeepDivePill
          stepId={finding.sourceStepId}
          onActionSourceClick={onActionSourceClick}
          label={actionSourceLabel}
        />
      </div>
      <RichTextRenderer content={finding.description} className={markdownP} />

      {actionRows.length > 0 ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {actionRows.map((action, index) => (
            <motion.div
              key={`${finding.id}-ac-${index}`}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: reduceMotion ? 0 : index * 0.05, duration: 0.25 }}
              whileHover={
                reduceMotion ? undefined : { y: -3, transition: { duration: 0.2 } }
              }
            >
              <Card className="group/ac h-full cursor-default border-custom-border/90 bg-gradient-to-br from-orange-50/90 to-amber-50/30 p-3.5 shadow-sm transition-shadow duration-300 hover:border-amber-300/50 hover:shadow-md dark:from-orange-950/35 dark:to-amber-950/15 dark:hover:border-amber-700/40">
                <div className="mb-2 flex items-start justify-between gap-2">
                  <Sparkles className="h-3.5 w-3.5 shrink-0 text-amber-500 transition-transform duration-200 group-hover/ac:rotate-12" aria-hidden />
                  <DeepDivePill
                    stepId={action.sourceStepId}
                    onActionSourceClick={onActionSourceClick}
                    label={actionSourceLabel}
                  />
                </div>
                <RichTextRenderer
                  content={action.title}
                  className="[&_p]:m-0 [&_p]:text-xs [&_p]:leading-relaxed [&_p]:text-custom-text-primary"
                />
              </Card>
            </motion.div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/** Step index + numbered list for actions. */
function NumberedStepsFinding({
  finding,
  stepIndex,
  reduceMotion,
  onActionSourceClick,
  actionSourceLabel,
}: {
  finding: AgentKeyFinding;
  stepIndex: number;
  reduceMotion: boolean;
  onActionSourceClick?: (sourceStepId: string) => void;
  actionSourceLabel: string;
}) {
  const actionRows = actionRowsForFinding(finding);

  return (
    <div className="flex gap-4">
      <motion.div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-rose-100 to-rose-50 text-sm font-bold text-rose-900 shadow-sm ring-2 ring-rose-200/80 dark:from-rose-950/80 dark:to-rose-900/50 dark:text-rose-100 dark:ring-rose-800/50"
        aria-hidden
        initial={reduceMotion ? false : { scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 400, damping: 24 }}
        whileHover={reduceMotion ? undefined : { scale: 1.06 }}
      >
        {stepIndex + 1}
      </motion.div>
      <div className="min-w-0 flex-1 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <RichTextRenderer
              content={finding.title}
              className="[&_p]:m-0 [&_p]:text-sm [&_p]:font-semibold [&_p]:leading-snug [&_p]:text-custom-text-primary"
            />
          </div>
          <DeepDivePill
            stepId={finding.sourceStepId}
            onActionSourceClick={onActionSourceClick}
            label={actionSourceLabel}
          />
        </div>
        <RichTextRenderer content={finding.description} className={markdownP} />

        {actionRows.length > 0 ? (
          <ol className="list-decimal space-y-2 border-l-2 border-transparent pl-5 text-sm text-custom-text-primary marker:font-semibold marker:text-rose-600 dark:marker:text-rose-400">
            {actionRows.map((action, index) => (
              <motion.li
                key={`${finding.id}-num-${index}`}
                className="rounded-r-md py-0.5 pl-1 transition-colors hover:bg-rose-50/50 dark:hover:bg-rose-950/30"
                initial={reduceMotion ? false : { opacity: 0, x: -4 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: reduceMotion ? 0 : 0.06 * index + 0.1 }}
              >
                <span className="inline-flex w-full flex-wrap items-start justify-between gap-2">
                  <RichTextRenderer
                    content={action.title}
                    className="[&_p]:m-0 [&_p]:inline [&_p]:text-sm [&_p]:leading-relaxed"
                  />
                  <DeepDivePill
                    stepId={action.sourceStepId}
                    onActionSourceClick={onActionSourceClick}
                    label={actionSourceLabel}
                  />
                </span>
              </motion.li>
            ))}
          </ol>
        ) : null}
      </div>
    </div>
  );
}

/** One row in the timeline rail (dots + content). */
function TimelineFindingRow({
  finding,
  index,
  reduceMotion,
  onActionSourceClick,
  actionSourceLabel,
}: {
  finding: AgentKeyFinding;
  index: number;
  reduceMotion: boolean;
  onActionSourceClick?: (sourceStepId: string) => void;
  actionSourceLabel: string;
}) {
  const actionRows = actionRowsForFinding(finding);

  return (
    <motion.div
      className="relative pb-8 last:pb-0"
      initial={reduceMotion ? false : { opacity: 0, x: 8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: reduceMotion ? 0 : index * 0.08, duration: 0.35 }}
    >
      <motion.div
        className="absolute left-0 top-1.5 z-[1] h-3.5 w-3.5 rounded-full border-2 border-background bg-gradient-to-br from-rose-500 to-rose-600 shadow-md ring-2 ring-rose-200/90 dark:from-rose-600 dark:to-rose-700 dark:ring-rose-900/80"
        aria-hidden
        whileHover={reduceMotion ? undefined : { scale: 1.25 }}
        transition={{ type: 'spring', stiffness: 500, damping: 22 }}
      />
      <div className="min-w-0 space-y-3 pl-9">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <RichTextRenderer
              content={finding.title}
              className="[&_p]:m-0 [&_p]:text-sm [&_p]:font-semibold [&_p]:text-custom-text-primary"
            />
          </div>
          <DeepDivePill
            stepId={finding.sourceStepId}
            onActionSourceClick={onActionSourceClick}
            label={actionSourceLabel}
          />
        </div>
        <RichTextRenderer content={finding.description} className={markdownP} />
        {actionRows.length > 0 ? (
          <ul className="space-y-1.5 border-l-2 border-amber-200/90 pl-3.5 dark:border-amber-700/50">
            {actionRows.map((action, idx) => (
              <li
                key={`${finding.id}-tl-${idx}`}
                className="group/tl flex gap-2 rounded-r-md py-1 transition-colors hover:bg-amber-50/40 dark:hover:bg-amber-950/20"
              >
                <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-500 transition-transform group-hover/tl:scale-110" aria-hidden />
                <div className="flex min-w-0 flex-1 items-start justify-between gap-2">
                  <RichTextRenderer content={action.title} className={markdownP} />
                  <DeepDivePill
                    stepId={action.sourceStepId}
                    onActionSourceClick={onActionSourceClick}
                    label={actionSourceLabel}
                  />
                </div>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </motion.div>
  );
}

/**
 * Presents post-run findings in several layouts: stacked cards, todo checklist, action grid, numbered steps, or timeline.
 */
export const AgentKeyFindingsCard: React.FC<AgentKeyFindingsCardProps> = ({
  title,
  findings,
  layout = 'stacked',
  className,
  onPrimaryCta,
  onSecondaryCta,
  onActionSourceClick,
  actionSourceLabel = DEFAULT_ACTION_SOURCE_LABEL,
}) => {
  const reduceMotion = useReducedMotion();

  const sectionTitle =
    layout === 'actionChecklist' ||
    layout === 'taskRows' ||
    layout === 'taskCards' ||
    layout === 'agentActionCards'
      ? title ?? ACTION_ITEMS_TITLE
      : title ?? DEFAULT_TITLE;

  const headingClass =
    layout === 'agentActionCards'
      ? 'text-xl font-bold leading-snug text-custom-text-primary'
      : 'border-b border-rose-200/40 pb-2 text-lg font-semibold leading-snug text-custom-text-primary dark:border-rose-900/40';

  const hideOuterSectionHeading =
    layout === 'actionChecklist' || layout === 'taskRows';

  if (!findings?.length) {
    return null;
  }

  if (layout === 'actionChecklist' && flattenChecklistRows(findings).length === 0) {
    return null;
  }

  if (
    (layout === 'taskRows' || layout === 'taskCards') &&
    flattenTaskRows(findings).length === 0
  ) {
    return null;
  }

  const itemMotion = (i: number): Pick<
    HTMLMotionProps<'div'>,
    'initial' | 'animate' | 'transition'
  > =>
    reduceMotion
      ? {}
      : {
          initial: { opacity: 0, y: 14 },
          animate: { opacity: 1, y: 0 },
          transition: {
            duration: 0.35,
            ease: [0.25, 0.1, 0.25, 1],
            delay: i * 0.07,
          },
        };

  const taskRowsFlat = flattenTaskRows(findings);

  return (
    <section className={cn(SECTION_SHELL, className)} aria-label={sectionTitle}>
      {!hideOuterSectionHeading ? (
        <motion.div
          initial={reduceMotion ? false : { opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] as const }}
        >
          <h2 className={headingClass}>{sectionTitle}</h2>
        </motion.div>
      ) : null}

      <div
        className={cn(
          !hideOuterSectionHeading && 'mt-5',
          'flex flex-col gap-5',
        )}
      >
        {layout === 'actionChecklist' && (
          <ActionChecklistLayout
            title={sectionTitle}
            findings={findings}
            motionProps={itemMotion(0)}
            reduceMotion={!!reduceMotion}
            onActionSourceClick={onActionSourceClick}
            actionSourceLabel={actionSourceLabel}
          />
        )}

        {layout === 'taskRows' && (
          <TaskRowsLayout
            title={sectionTitle}
            rows={taskRowsFlat}
            motionProps={itemMotion(0)}
            reduceMotion={!!reduceMotion}
            onActionSourceClick={onActionSourceClick}
            actionSourceLabel={actionSourceLabel}
          />
        )}

        {layout === 'taskCards' && (
          <TaskCardsLayout
            rows={taskRowsFlat}
            itemMotion={itemMotion}
            onActionSourceClick={onActionSourceClick}
            actionSourceLabel={actionSourceLabel}
          />
        )}

        {layout === 'agentActionCards' && (
          <AgentActionCardsLayout
            findings={findings}
            itemMotion={itemMotion}
            onPrimaryCta={onPrimaryCta}
            onSecondaryCta={onSecondaryCta}
            onActionSourceClick={onActionSourceClick}
            actionSourceLabel={actionSourceLabel}
          />
        )}

        {layout === 'stacked' &&
          findings.map((finding, i) => (
            <StackedFinding
              key={finding.id}
              finding={finding}
              motionProps={itemMotion(i)}
              reduceMotion={!!reduceMotion}
              onActionSourceClick={onActionSourceClick}
              actionSourceLabel={actionSourceLabel}
            />
          ))}

        {layout === 'todoList' &&
          findings.map((finding, i) => (
            <TodoListFinding
              key={finding.id}
              finding={finding}
              motionProps={itemMotion(i)}
              onActionSourceClick={onActionSourceClick}
              actionSourceLabel={actionSourceLabel}
            />
          ))}

        {layout === 'actionCards' &&
          findings.map((finding, i) => (
            <motion.div key={finding.id} {...itemMotion(i)}>
              <Card
                className={cn(
                  'rounded-xl border-custom-border bg-gradient-to-b from-custom-bg-subtle/90 to-background/70 p-5 shadow-sm dark:from-zinc-900/60 dark:to-zinc-950/50',
                  cardInteractive,
                )}
              >
                <ActionCardsFindingWithMotion
                  finding={finding}
                  reduceMotion={!!reduceMotion}
                  onActionSourceClick={onActionSourceClick}
                  actionSourceLabel={actionSourceLabel}
                />
              </Card>
            </motion.div>
          ))}

        {layout === 'numberedSteps' &&
          findings.map((finding, index) => (
            <motion.div key={finding.id} {...itemMotion(index)}>
              <Card
                className={cn(
                  'rounded-xl border-custom-border bg-background/95 p-5 shadow-sm',
                  cardInteractive,
                )}
              >
                <NumberedStepsFinding
                  finding={finding}
                  stepIndex={index}
                  reduceMotion={!!reduceMotion}
                  onActionSourceClick={onActionSourceClick}
                  actionSourceLabel={actionSourceLabel}
                />
              </Card>
            </motion.div>
          ))}

        {layout === 'timeline' && (
          <motion.div {...(reduceMotion ? {} : itemMotion(0))}>
            <Card
              className={cn(
                'rounded-xl border-custom-border bg-gradient-to-b from-background to-muted/20 p-5 shadow-md dark:to-zinc-950/30',
                cardInteractive,
              )}
            >
              <div className="relative pl-1">
                <div
                  className="absolute left-[6px] top-3 bottom-3 w-px bg-gradient-to-b from-rose-300 via-border to-amber-200/80 dark:from-rose-800 dark:via-zinc-700 dark:to-amber-900/50"
                  aria-hidden
                />
                {findings.map((finding, index) => (
                  <TimelineFindingRow
                    key={finding.id}
                    finding={finding}
                    index={index}
                    reduceMotion={!!reduceMotion}
                    onActionSourceClick={onActionSourceClick}
                    actionSourceLabel={actionSourceLabel}
                  />
                ))}
              </div>
            </Card>
          </motion.div>
        )}
      </div>
    </section>
  );
};
